import { useState, type ReactNode } from "react";
import type { RecordView } from "@shared-core/domain/object-ui/contract";
import {
  formatPence,
  invoicesThisFirst,
  matchingOf,
  type MatchingPair,
  type MatchingView,
} from "@shared-core/domain/purchase-invoice-matching";
import { paths } from "@shared-core/routing/routes";
import { Link } from "react-router";
import { Card, CardContent } from "../../primitives/card";
import { cn } from "../../primitives/utils";

const INVOICE = "finance_purchase_ledger.purchase_invoice";
const PURCHASE_ORDER = "purchase_orders.purchase_order";

/**
 * A group with more pairs than this shows only the pairs that need a look until the person asks for the rest: in a
 * hundred-line invoice the ninety-six that match say nothing a person must read.
 */
const SHOW_ALL_ABOVE_PAIRS = 12;

const MARK: Record<MatchingPair["state"], [string, string]> = {
  ok: ["✓", "text-emerald-700 dark:text-emerald-400"],
  warn: ["!", "text-amber-700 dark:text-amber-400"],
  fail: ["?", "text-red-700 dark:text-red-400"],
  spread: ["~", "text-muted-foreground"],
};
const TONE: Record<MatchingView["tone"], string> = {
  ok: "text-emerald-700 dark:text-emerald-400",
  warn: "text-amber-700 dark:text-amber-400",
  fail: "text-red-700 dark:text-red-400",
};

interface DocumentRow {
  key: string;
  reference: string;
  description: string;
  amount_pence: number;
  detail: ReactNode;
}

/**
 * One document of the group, an invoice or a purchase order, drawn the same way on both sides so the two read
 * across: its number and what it is for, then each line's reference, description and amount, with a detail line.
 */
function DocumentCard({
  title,
  subtitle,
  total_pence,
  rows,
}: {
  title: ReactNode;
  subtitle: string | null;
  total_pence: number | null;
  rows: DocumentRow[];
}) {
  return (
    <div className="rounded-md border border-border px-3 py-2 text-sm">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2">
        <p className="font-medium tabular-nums">
          {title}
          {subtitle && (
            <span className="font-normal text-muted-foreground">
              {" "}
              {subtitle}
            </span>
          )}
        </p>
        {total_pence !== null && (
          <span className="text-right text-muted-foreground tabular-nums">
            {formatPence(total_pence)}
          </span>
        )}
      </div>
      <ul className="mt-1 space-y-1">
        {rows.map((row) => (
          <li
            key={row.key}
            className="grid grid-cols-[2.25rem_minmax(0,1fr)_auto] gap-x-2"
          >
            <span className="text-muted-foreground tabular-nums">
              {row.reference}
            </span>
            <span className="min-w-0">
              {row.description}
              <span className="block text-xs text-muted-foreground">
                {row.detail}
              </span>
            </span>
            <span className="text-right tabular-nums">
              {formatPence(row.amount_pence)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ColumnHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

/**
 * A purchase invoice's match group, drawn on the record of each invoice in it (review
 * `docs/features/purchase-invoice-matching/13_matching_in_one_review.md` §5): the group's invoices against its
 * orders, line by line, the group's figures and why it differs. It draws the ledger's facts only and has no write of
 * its own and draws no commands: Edit matching… and every other command are the record's header's (Ben, 28 Oct 2026).
 */
export function MatchingSection({ record }: { record: RecordView }) {
  const matching = matchingOf(record);
  const [isAllShown, setAllShown] = useState(false);
  if (!matching) return null;
  const isLong = matching.pairs.length > SHOW_ALL_ABOVE_PAIRS;
  const isCollapsed = isLong && !isAllShown;
  // Collapsed, an order line is left out when every pair on it matches exactly: nothing to read there either.
  const exact = matching.pairs.filter((pair) => pair.state === "ok");
  const lookedAt = new Set(
    matching.pairs
      .filter((pair) => pair.state !== "ok")
      .map((pair) => pair.order_line_reference),
  );
  const matchedExactly = new Set(
    exact
      .map((pair) => pair.order_line_reference)
      .filter((reference) => !lookedAt.has(reference)),
  );
  return (
    <Card>
      <CardContent className="space-y-3 py-4 max-sm:px-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="text-base font-semibold">
            Matching
            {matching.headline && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {matching.headline}
              </span>
            )}
          </h2>
          <span
            className={cn(
              "text-sm font-semibold tabular-nums",
              TONE[matching.tone],
            )}
          >
            {matching.summary}
          </span>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <section aria-label="Invoices" className="space-y-2">
            <ColumnHeading>
              {matching.invoices.length > 1 ? "Invoices" : "Invoice"}
            </ColumnHeading>
            {/* The invoice whose record this is comes first; the label says which it is. */}
            {invoicesThisFirst(matching).map((invoice) => (
              <DocumentCard
                key={invoice.purchase_invoice_id}
                title={
                  invoice.is_this ? (
                    invoice.number
                  ) : (
                    <Link
                      className="underline-offset-2 hover:underline"
                      to={paths.objectRecord({
                        objectId: INVOICE,
                        recordKey: invoice.purchase_invoice_id,
                      })}
                    >
                      {invoice.number}
                    </Link>
                  )
                }
                subtitle={invoice.label}
                total_pence={invoice.net_pence}
                rows={matching.pairs
                  .filter((pair) => pair.invoice_number === invoice.number)
                  .filter((pair) => !isCollapsed || pair.state !== "ok")
                  .map((pair) => {
                    const [mark, markClass] = MARK[pair.state];
                    return {
                      key: `${pair.invoice_number}.${pair.line_number}`,
                      reference: `.${pair.line_number}`,
                      description: pair.description,
                      amount_pence: pair.amount_pence,
                      detail: (
                        <>
                          <span
                            aria-hidden
                            className={cn("mr-1 font-semibold", markClass)}
                          >
                            {mark}
                          </span>
                          {pair.order_line_reference
                            ? `→ ${pair.order_line_reference}`
                            : null}
                          {pair.order_line_reference && pair.note ? " · " : ""}
                          {pair.note}
                        </>
                      ),
                    };
                  })}
              />
            ))}
          </section>

          <section aria-label="Purchase orders" className="space-y-2">
            <ColumnHeading>
              {matching.orders.length > 1
                ? "Purchase orders"
                : "Purchase order"}
            </ColumnHeading>
            {matching.orders.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No purchase order matched yet. Edit matching… suggests the
                supplier's open orders, each with why it fits.
              </p>
            )}
            {matching.orders.map((order) => (
              <DocumentCard
                key={order.purchase_order_no}
                title={
                  <Link
                    className="underline-offset-2 hover:underline"
                    to={paths.objectRecord({
                      objectId: PURCHASE_ORDER,
                      recordKey: order.purchase_order_no,
                    })}
                  >
                    {order.purchase_order_no}
                  </Link>
                }
                subtitle={
                  order.customer_order_no
                    ? `for ${order.customer_order_no}${order.customer_name ? `, ${order.customer_name}` : ""}`
                    : null
                }
                total_pence={order.lines.reduce(
                  (sum, line) => sum + line.net_pence,
                  0,
                )}
                rows={order.lines
                  .filter(
                    (line) =>
                      !isCollapsed || !matchedExactly.has(line.reference),
                  )
                  .map((line) => ({
                    key: line.reference,
                    reference: line.reference.slice(
                      order.purchase_order_no.length,
                    ),
                    description: line.description,
                    amount_pence: line.net_pence,
                    detail: `${line.progress}${
                      line.left_pence
                        ? ` · ${formatPence(line.left_pence)} left to bill`
                        : ""
                    }`,
                  }))}
              />
            ))}
          </section>
        </div>

        {isLong && (
          <p className="text-sm text-muted-foreground">
            {isCollapsed ? (
              <>
                {exact.length} lines match their order lines exactly ·{" "}
                <button
                  type="button"
                  className="text-primary underline hover:no-underline"
                  onClick={() => setAllShown(true)}
                >
                  Show all {matching.pairs.length}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="text-primary underline hover:no-underline"
                onClick={() => setAllShown(false)}
              >
                Show only the lines that need a look
              </button>
            )}
          </p>
        )}

        {matching.figures && (
          <p className="border-t border-border pt-2 text-sm tabular-nums text-muted-foreground">
            Ordered {formatPence(matching.figures.ordered_pence)} · billed
            before {formatPence(matching.figures.billed_before_pence)} · this
            round {formatPence(matching.figures.this_round_pence)} ·{" "}
            <span className={cn("font-medium", TONE[matching.tone])}>
              {matching.figures.difference_pence > 0
                ? `over ${formatPence(matching.figures.difference_pence)}`
                : matching.figures.difference_pence < 0
                  ? `under ${formatPence(-matching.figures.difference_pence)}`
                  : "over £0.00"}
              {matching.figures.difference_percent !== null &&
              matching.figures.difference_pence !== 0
                ? ` (${matching.figures.difference_percent}%)`
                : ""}
            </span>
          </p>
        )}

        {matching.causes.length > 0 && (
          <div className="space-y-1 text-sm">
            <h3 className="font-medium">Why it differs</h3>
            <ul className="space-y-0.5">
              {[
                ...matching.causes,
                {
                  label: "Not explained",
                  sentence: "",
                  amount_pence: matching.not_explained_pence ?? 0,
                },
              ].map((cause, n) => (
                <li
                  key={`${cause.label}-${n}`}
                  className="grid grid-cols-[7rem_minmax(0,1fr)_auto] gap-x-2"
                >
                  <span className="font-medium">{cause.label}</span>
                  <span>{cause.sentence}</span>
                  <span className="text-right tabular-nums">
                    {formatPence(cause.amount_pence)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {matching.notes.map((note) => (
          <p key={note} className="text-sm">
            {note}
          </p>
        ))}
      </CardContent>
    </Card>
  );
}
